import { useCallback, useState } from 'react'
import { useHuddle } from '../../contexts/HuddleContext'
import type { ListMentionSuggestions } from '../../hooks/useMentionSuggestions'
import UserPicker from '../../pages/Messages/components/UserPicker'
import { useDispatch } from '../../redux/hooks'
import { listMentionSuggestions } from '../../redux/mentions/actions'
import Button from '../Common/Button'
import Icon from '../Common/Icon'
import IconButton from '../Common/IconButton'
import { IMentionSuggestion } from '../Common/Mentions/types'
import Modal from '../Common/Modal'

/**
 * Ring anyone into the huddle. Someone not in the conversation yet is added to it first,
 * so they can join and read along afterwards.
 */
const HuddleInviteButton = () => {
  const dispatch = useDispatch()
  const { snapshot, invite } = useHuddle()
  const [isOpen, setIsOpen] = useState(false)
  const [picked, setPicked] = useState<IMentionSuggestion[]>([])
  const listPeople = useCallback<ListMentionSuggestions>(
    (params) => dispatch(listMentionSuggestions(params)) as ReturnType<ListMentionSuggestions>,
    [dispatch],
  )
  const handleOpen = useCallback(() => setIsOpen(true), [])
  const handleClose = useCallback(() => {
    setIsOpen(false)
    setPicked([])
  }, [])
  const handleInvite = useCallback(() => {
    if (picked.length === 0) return
    invite(picked.map(({ id }) => id))
    handleClose()
  }, [handleClose, invite, picked])

  const inHuddle = snapshot?.huddle?.peers.map(({ userId }) => userId) ?? []
  return (
    <>
      <IconButton title="Invite to the huddle" box="sm" onClick={handleOpen}>
        <Icon name="user-plus" size="sm" />
      </IconButton>
      {isOpen && (
        <Modal
          open
          title="Invite to the huddle"
          size="small"
          onCancel={handleClose}
          footer={
            <>
              <Button secondary onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleInvite} disabled={picked.length === 0}>
                Invite
              </Button>
            </>
          }
        >
          <UserPicker
            label="People"
            placeholder="Search anyone"
            value={picked}
            onChange={setPicked}
            excludeUserIds={inHuddle}
            listMentionSuggestions={listPeople}
            onSubmit={handleInvite}
            autoFocus
          />
        </Modal>
      )}
    </>
  )
}

export default HuddleInviteButton
